import React, { useState, useEffect } from 'react';
import { IconSearch, IconFilter, IconEye, IconCheck, IconX } from '../components/Icons';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, updateDoc, setDoc, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../firebase';

const STATUS_STYLE = {
  'Delivered': 'success',
  'On the way': 'info',
  'Picked': 'warning',
  'Accepted': 'primary',
  'Ordered': 'muted',
  'Rejected': 'danger',
};

const FILTERS = ['All', 'Ordered', 'Accepted', 'Picked', 'On the way', 'Delivered', 'Rejected'];

export default function Orders() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('All');
  const [selected, setSelected] = useState(null);
  const [orders, setOrders] = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  // Call Order State
  const [shops, setShops] = useState([]);
  const [showCallOrderModal, setShowCallOrderModal] = useState(false);
  const [callOrderForm, setCallOrderForm] = useState({
    customerPhone: '',
    itemsDesc: '',
    amount: '',
    shopId: '',
    village: '',
    landmark: ''
  });

  useEffect(() => {
    // Fetch delivery boys for assignment
    const qDB = query(collection(db, 'users'));
    const unsubDB = onSnapshot(qDB, snap => {
      setDeliveryBoys(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => (u.role === 'deliveryboy' || u.role === 'Delivery Boy') && u.status === 'Active')
      );
    });

    // Fetch shops for Call Orders
    const qShops = query(collection(db, 'users'), where('role', '==', 'shopkeeper'));
    const unsubShops = onSnapshot(qShops, snap => {
      setShops(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    
    const q = query(collection(db, 'orders'), orderBy('placedAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setOrders(data);
      setLoading(false);
    }, err => {
      console.error("Firestore error:", err);
      setErrorMsg(err.message);
      setLoading(false);
    });
    return () => { unsub(); unsubDB(); unsubShops(); };
  }, []);

  // Helper to check date
  const isSameDay = (d1, d2) => {
    return d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear();
  };

  // Helper to format date
  const formatOrderDate = (ts) => {
    if (!ts) return 'Just now';
    const d = ts.toDate();
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    if (isSameDay(d, today)) return `Today, ${time}`;
    if (isSameDay(d, yesterday)) return `Yesterday, ${time}`;
    return `${d.toLocaleDateString('en-IN')} ${time}`;
  };

  // Delete demo orders (status 'Ordered')
  const deleteDemoData = async () => {
    const demoDocs = orders.filter(o => o.status === 'Ordered');
    for (const o of demoDocs) {
      await deleteDoc(doc(db, 'orders', o.id));
    }
    alert('Demo orders deleted');
  };

  const filtered = orders.filter(o => {
    const shopStr = typeof o.shop === 'object' ? o.shop?.name : o.shop;
    const matchSearch = o.phone?.includes(search) ||
      o.id?.toLowerCase().includes(search.toLowerCase()) ||
      shopStr?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'All' || o.status === filter;

    // Date Filter logic
    let matchDate = true;
    if (o.placedAt) {
      const orderDate = o.placedAt.toDate();
      const today = new Date();
      if (dateFilter === 'Today') {
        matchDate = isSameDay(orderDate, today);
      } else if (dateFilter === 'Yesterday') {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        matchDate = isSameDay(orderDate, yesterday);
      }
    } else if (dateFilter !== 'All') {
      matchDate = false;
    }

    return matchSearch && matchFilter && matchDate;
  });

  const [manualBoy, setManualBoy] = useState('');
  
  // AI Auto-Dispatch States
  const [showAIDispatchModal, setShowAIDispatchModal] = useState(false);
  const [aiLogs, setAiLogs] = useState([]);
  const [aiProposedMatches, setAiProposedMatches] = useState([]);
  const [aiStatus, setAiStatus] = useState('idle'); // 'idle' | 'scanning' | 'calculated' | 'saving' | 'complete'

  // Haversine distance calculator
  const getDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 2.0; // Default fallback to 2km if coordinates missing
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    return R * c;
  };

  const getShopCoords = (shopId) => {
    const shopObj = shops.find(s => s.id === shopId);
    if (shopObj) {
      if (shopObj.coords) return shopObj.coords;
      if (shopObj.latitude && shopObj.longitude) return { latitude: shopObj.latitude, longitude: shopObj.longitude };
      if (shopObj.lat && shopObj.lng) return { latitude: shopObj.lat, longitude: shopObj.lng };
    }
    return { latitude: 17.4332, longitude: 75.1979 }; // Sangola center fallback
  };

  const getRiderCoords = (rider) => {
    if (rider.currentLocation) return rider.currentLocation;
    if (rider.coords) return rider.coords;
    if (rider.latitude && rider.longitude) return { latitude: rider.latitude, longitude: rider.longitude };
    if (rider.lat && rider.lng) return { latitude: rider.lat, longitude: rider.lng };
    // Simulated offset from Sangola Center so they are spread out
    return { latitude: 17.4332 + (Math.random() - 0.5) * 0.015, longitude: 75.1979 + (Math.random() - 0.5) * 0.015 };
  };

  const runAIDispatch = () => {
    const unassigned = orders.filter(o => o.status === 'Ordered');
    if (unassigned.length === 0) {
      alert("सर्व ऑर्डर्स आधीच असाइन केल्या आहेत! | All orders are already assigned!");
      return;
    }
    
    setAiProposedMatches([]);
    setAiLogs([]);
    setAiStatus('scanning');
    setShowAIDispatchModal(true);

    let logs = ["🤖 AI Dispatch Engine initialized...", "🔍 Scanning for unassigned orders..."];
    setAiLogs([...logs]);

    setTimeout(() => {
      logs.push(`Found ${unassigned.length} unassigned order(s).`);
      logs.push("🛵 Fetching active, online delivery boys...");
      setAiLogs([...logs]);

      setTimeout(() => {
        const activeRiders = deliveryBoys.filter(r => r.status === 'Active');
        logs.push(`Found ${activeRiders.length} active delivery boy(s) online.`);
        setAiLogs([...logs]);

        if (activeRiders.length === 0) {
          logs.push("❌ Error: No active delivery boys found online. Dispatch cancelled.");
          setAiLogs([...logs]);
          setAiStatus('idle');
          return;
        }

        setTimeout(() => {
          logs.push("🧠 Running Match Score matching algorithm...");
          setAiLogs([...logs]);

          const proposed = [];
          unassigned.forEach(order => {
            logs.push(`Evaluating matches for Order ${order.id}...`);
            
            const shopId = order.shopId || (order.shop && typeof order.shop === 'object' ? order.shop.id : null);
            const shopCoords = getShopCoords(shopId);
            
            // Calculate active workloads for riders
            const riderLoad = {};
            orders.forEach(o => {
              if (o.deliveryBoyId && ['Accepted', 'Picked', 'On the way'].includes(o.status)) {
                riderLoad[o.deliveryBoyId] = (riderLoad[o.deliveryBoyId] || 0) + 1;
              }
            });

            // Calculate matching score for each rider
            const candidates = activeRiders.map(rider => {
              const riderCoords = getRiderCoords(rider);
              const dist = getDistance(
                riderCoords.latitude, riderCoords.longitude,
                shopCoords.latitude, shopCoords.longitude
              );
              
              const load = riderLoad[rider.id] || 0;
              const orderVillage = (order.address && typeof order.address === 'object' ? order.address.village : order.village) || 'Sangola';
              const riderVillage = rider.village || 'Sangola';
              const villageMatch = orderVillage.toLowerCase() === riderVillage.toLowerCase();

              // Multi-criteria heuristic scoring
              let score = 100;
              score -= dist * 8; // Penalty: -8 points per km distance
              score -= load * 20; // Penalty: -20 points per current active order load
              if (villageMatch) score += 15; // Bonus: +15 points for same-village home base matching
              score += (parseFloat(rider.rating) || 5.0) * 3; // Bonus: +15 points max for ratings
              
              return {
                rider,
                score: Math.max(0, Math.min(100, Math.round(score))),
                dist: dist.toFixed(2),
                load
              };
            });

            // Sort descending by score
            candidates.sort((a, b) => b.score - a.score);
            const best = candidates[0];
            
            logs.push(`  → Recommendation: ${best.rider.name} (Score: ${best.score}/100, Dist: ${best.dist}km, Workload: ${best.load} tasks)`);
            proposed.push({
              orderId: order.id,
              orderDesc: typeof order.product === 'object' ? order.product?.name : (order.item || 'Grocery Item'),
              village: (order.address && typeof order.address === 'object' ? order.address.village : order.village) || 'Sangola',
              riderId: best.rider.id,
              riderName: best.rider.name,
              riderPhone: best.rider.phone || '',
              score: best.score,
              dist: best.dist,
              load: best.load
            });
          });

          setAiProposedMatches(proposed);
          logs.push("✅ Optimal matching proposals computed!");
          setAiLogs([...logs]);
          setAiStatus('calculated');
        }, 1000);
      }, 1000);
    }, 1000);
  };

  const confirmAIDispatch = async () => {
    setAiStatus('saving');
    let logs = [...aiLogs, "💾 Applying matches to Firestore Database..."];
    setAiLogs(logs);

    try {
      for (const match of aiProposedMatches) {
        await updateDoc(doc(db, 'orders', match.orderId), {
          deliveryBoyId: match.riderId,
          deliveryBoyName: match.riderName,
          deliveryBoy: match.riderName, // Keep legacy field
          deliveryBoyPhone: match.riderPhone,
          deliveryBoyAccepted: false,
          status: 'Accepted'
        });
      }
      setAiStatus('complete');
      setAiLogs([...logs, "🎉 All orders successfully assigned and notified!"]);
      setTimeout(() => {
        setShowAIDispatchModal(false);
        alert("AI द्वारे सर्व ऑर्डर्स असाइन केल्या आहेत (स्वीकृतीची प्रतीक्षा आहे)!");
      }, 1000);
    } catch (e) {
      alert("Error saving: " + e.message);
      setAiStatus('calculated');
    }
  };

  const assignOrder = async (orderId) => {
    if (!manualBoy) return alert("कृपया डिलिव्हरी बॉय निवडा");
    const boy = deliveryBoys.find(b => b.id === manualBoy);
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        deliveryBoyId: boy.id,
        deliveryBoyName: boy.name,
        deliveryBoy: boy.name, // Keep legacy field
        deliveryBoyPhone: boy.phone || '',
        deliveryBoyAccepted: false,
        status: 'Accepted'
      });
      alert("ऑर्डर यशस्वीरीत्या असाइन केली आहे (डिलिव्हरी बॉयच्या स्वीकृतीची प्रतीक्षा आहे)!");
      setSelected(null);
    } catch (e) {
      alert("Error: " + e.message);
    }
  };

  const cancelOrder = async (orderId) => {
    if (window.confirm("ही ऑर्डर रद्द करायची आहे का? (Fake Payment / Other)")) {
      try {
        await updateDoc(doc(db, 'orders', orderId), {
          status: 'Rejected',
          cancelReason: 'Cancelled by Admin (Payment Failed or Admin Choice)'
        });
        alert("ऑर्डर रद्द केली आहे!");
        setSelected(null);
      } catch (e) {
        alert("Error: " + e.message);
      }
    }
  };

  const handleCreateCallOrder = async () => {
    if (!callOrderForm.customerPhone || !callOrderForm.itemsDesc || !callOrderForm.shopId) {
      return alert("Customer Phone, Items, and Shop are required!");
    }

    const selectedShop = shops.find(s => s.id === callOrderForm.shopId);
    const orderId = 'ORD-' + Math.floor(Math.random() * 90000 + 10000);
    const deliveryOTP = Math.floor(1000 + Math.random() * 9000).toString();
    const amountNum = parseFloat(callOrderForm.amount) || 0;

    const newOrder = {
      id: orderId,
      phone: callOrderForm.customerPhone,
      customer: callOrderForm.customerPhone, // Legacy support
      shop: {
        id: selectedShop.id,
        name: selectedShop.shopName || selectedShop.name || 'Assigned Shop'
      },
      shopId: selectedShop.id,
      items: [{
        id: 'CALL-ITEM',
        name: callOrderForm.itemsDesc,
        quantity: 1,
        price: amountNum
      }],
      product: { name: callOrderForm.itemsDesc }, // Legacy
      amount: amountNum,
      itemsTotal: amountNum,
      deliveryFee: 0,
      appCommission: 0,
      shopkeeperAmount: amountNum,
      address: {
        village: callOrderForm.village || 'Sangola',
        landmark: callOrderForm.landmark || '',
        full: `${callOrderForm.landmark}, ${callOrderForm.village}`
      },
      paymentMethod: 'COD',
      status: 'Ordered',
      deliveryOTP,
      placedAt: serverTimestamp(),
      deliveryBoy: null,
      isCallOrder: true
    };

    try {
      await setDoc(doc(db, 'orders', orderId), newOrder);
      alert("कॉल ऑर्डर यशस्वीरीत्या तयार केली आणि दुकानाला पाठवली!");
      setShowCallOrderModal(false);
      setCallOrderForm({ customerPhone: '', itemsDesc: '', amount: '', shopId: '', village: '', landmark: '' });
    } catch (e) {
      alert("Error creating call order: " + e.message);
    }
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 12, width: '100%' }}>
          <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
            <IconSearch size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              placeholder="ऑर्डर शोधा | Search orders..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="btn btn-primary btn-sm" onClick={runAIDispatch} style={{ background: 'linear-gradient(135deg, #FF6B35, #E04E1D)', border: 'none', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}><span style={{ animation: 'pulse 1.5s infinite' }}>⚡</span> Run AI Dispatch</button>
          <button className="btn btn-success btn-sm" onClick={() => setShowCallOrderModal(true)}>📞 New Call Order</button>
          <button className="btn btn-danger btn-sm" onClick={deleteDemoData}>Delete Demo Data</button>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Date:</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { id: 'All', l: 'सर्व | All' },
              { id: 'Today', l: 'आजच्या | Today' },
              { id: 'Yesterday', l: 'कालच्या | Yesterday' }
            ].map(d => (
              <button key={d.id}
                className={`btn ${dateFilter === d.id ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                onClick={() => setDateFilter(d.id)}
              >{d.l}</button>
            ))}
          </div>

          <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 8px' }} />

          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Status:</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <button key={f}
                className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                onClick={() => setFilter(f)}
              >{f}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', count: orders.length, color: 'var(--text-primary)' },
          { label: 'Active', count: orders.filter(o => ['Ordered', 'Accepted', 'Picked', 'On the way'].includes(o.status)).length, color: 'var(--warning)' },
          { label: 'Delivered', count: orders.filter(o => o.status === 'Delivered').length, color: 'var(--success)' },
          { label: 'Rejected', count: orders.filter(o => o.status === 'Rejected').length, color: 'var(--danger)' },

        ].map((s, i) => (
          <div key={i} style={{
            padding: '10px 16px', background: 'var(--bg-card)',
            border: '1px solid var(--border)', borderRadius: 8,
            fontSize: 13
          }}>
            <span style={{ color: 'var(--text-muted)' }}>{s.label} </span>
            <span style={{ fontWeight: 700, color: s.color }}>{s.count}</span>
          </div>
        ))}
      </div>

      {errorMsg && (
        <div style={{ padding: 20, background: '#fee2e2', color: '#ef4444', borderRadius: 8, marginBottom: 20 }}>
          <strong>Error loading orders:</strong> {errorMsg}
        </div>
      )}

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Shop</th>
                <th>Items</th>
                <th>Amount</th>
                <th>Delivery Boy</th>
                <th>Zone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--primary)' }}>{o.id}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {formatOrderDate(o.placedAt)}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {typeof o.customer === 'object' ? (o.customer?.name || o.customer?.phone || o.phone || 'Unknown User') : (o.phone || o.customer || 'Unknown User')}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {typeof o.customer === 'object' ? (o.customer?.phone || '') : (typeof o.customer === 'string' ? o.customer : '')}
                    </div>
                  </td>
                  <td style={{ fontSize: 12 }}>{typeof o.shop === 'object' ? o.shop?.name : (o.shop || 'Unknown Shop')}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 140 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {typeof o.product === 'object' ? o.product?.name : (o.item || 'Item')}
                    </div>
                  </td>
                  <td style={{ fontWeight: 700, color: 'var(--success)' }}>
                    ₹{typeof o.amount === 'number' ? o.amount : String(o.amount || 0).replace(/[^0-9]/g, '')}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {(o.deliveryBoyName || o.deliveryBoy) && (o.deliveryBoyName !== '—' && o.deliveryBoy !== '—')
                      ? (
                        <div>
                          <div style={{ fontWeight: 600 }}>{o.deliveryBoyName || (typeof o.deliveryBoy === 'object' ? o.deliveryBoy?.name : o.deliveryBoy)}</div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: o.deliveryBoyAccepted ? 'var(--success)' : '#f59e0b', marginTop: 2 }}>
                            {o.deliveryBoyAccepted ? '✅ Accepted' : '⏳ Pending Accept'}
                          </div>
                          {o.deliveryBoyPhone && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>📞 {o.deliveryBoyPhone}</div>}
                        </div>
                      )
                      : <span style={{ color: 'var(--text-muted)' }}>Not assigned</span>
                    }
                  </td>
                  <td>
                    <span style={{
                      fontSize: 11, background: 'var(--bg)', padding: '3px 8px',
                      borderRadius: 6, color: 'var(--text-muted)', border: '1px solid var(--border)'
                    }}>
                      {typeof o.address === 'object' ? (o.address?.village || o.address?.full) : (o.zone || 'Sangola')}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${STATUS_STYLE[o.status]}`}>{o.status}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="btn btn-secondary btn-sm btn-icon"
                        title="View Details"
                        onClick={() => setSelected(o)}
                      >
                        <IconEye size={13} />
                      </button>
                      {o.status === 'Ordered' && (
                        <button className="btn btn-success btn-sm btn-icon" title="Assign Delivery">
                          <IconCheck size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Detail Modal */}
      {selected && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 20
        }} onClick={() => setSelected(null)}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 16, padding: 28, width: '100%', maxWidth: 480
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{selected.id}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{selected.placedAt?.toDate().toLocaleString('en-US') || selected.time}</div>
              </div>
              <span className={`badge ${STATUS_STYLE[selected.status]}`} style={{ alignSelf: 'flex-start' }}>
                {selected.status}
              </span>
            </div>

            {/* Order Steps */}
            <div className="status-steps" style={{ marginBottom: 24 }}>
              {['Ordered', 'Accepted', 'Picked', 'On the way', 'Delivered'].map((step, i) => {
                const steps = ['Ordered', 'Accepted', 'Picked', 'On the way', 'Delivered'];
                const curIdx = steps.indexOf(selected.status);
                const stepIdx = i;
                const state = stepIdx < curIdx ? 'done' : stepIdx === curIdx ? 'active' : 'pending';
                return (
                  <div key={step} className={`step ${state}`}>
                    {i < 4 && <div className="step-line" />}
                    <div className="step-icon">{state === 'done' ? '✓' : i + 1}</div>
                    <div className="step-label">{step}</div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
              {[
                ['👤 Customer', typeof selected.customer === 'object' ? (selected.customer?.name || selected.customer?.phone || selected.phone || 'Unknown') : (selected.phone || selected.customer || 'Unknown')],
                ['📞 Phone', selected.phone || 'N/A'],
                ['🏪 Shop', typeof selected.shop === 'object' ? selected.shop?.name : (selected.shop || 'Unknown Shop')],
                ['🛵 Delivery Boy', selected.deliveryBoyName || (typeof selected.deliveryBoy === 'object' ? selected.deliveryBoy?.name : (selected.deliveryBoy || 'Unassigned'))],
                ['📞 Boy Phone', selected.deliveryBoyPhone || 'N/A'],
                ['📦 Items', typeof selected.product === 'object' ? selected.product?.name : (selected.item || 'Item')],
                ['💰 Amount', `₹${typeof selected.amount === 'number' ? selected.amount : String(selected.amount || 0).replace(/[^0-9]/g, '')}`],
                ['💳 Payment', `${selected.paymentMethod} ${selected.utrNumber ? `(UTR: ${selected.utrNumber})` : ''}`],
                ['📍 Address', typeof selected.address === 'object' ? (selected.address?.full || selected.address?.village) : (selected.address || 'Unknown')],
                ['📏 Zone', `${typeof selected.address === 'object' ? selected.address?.village : (selected.zone || 'Sangola')} | Est: ${selected.estimatedTime || selected.est || '30 min'}`],
              ].map(([lbl, val]) => (
                <div key={lbl} style={{ background: 'var(--bg)', padding: '10px 12px', borderRadius: 8 }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 2 }}>{lbl}</div>
                  <div style={{ fontWeight: 500 }}>{val}</div>
                </div>
              ))}
            </div>

            {['Ordered', 'Accepted'].includes(selected.status) && (
              <div style={{ marginTop: 24, padding: 16, background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>🚚 मॅन्युअली असाइन करा (Manual Assign)</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select 
                    value={manualBoy} 
                    onChange={e => setManualBoy(e.target.value)}
                    style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text)' }}
                  >
                    <option value="">डिलिव्हरी बॉय निवडा...</option>
                    {deliveryBoys.map(b => (
                      <option key={b.id} value={b.id}>{b.name} ({b.village || 'Sangola'})</option>
                    ))}
                  </select>
                  <button 
                    className="btn btn-primary"
                    onClick={() => assignOrder(selected.id)}
                  >असाइन करा</button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              {!['Delivered', 'Rejected'].includes(selected.status) && (
                <button
                  className="btn btn-danger w-full"
                  style={{ justifyContent: 'center', flex: 1 }}
                  onClick={() => cancelOrder(selected.id)}
                >Cancel Order</button>
              )}
              <button
                className="btn btn-secondary w-full"
                style={{ justifyContent: 'center', flex: 1 }}
                onClick={() => setSelected(null)}
              >Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Call Order Modal */}
      {showCallOrderModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 20
        }} onClick={() => setShowCallOrderModal(false)}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 16, padding: 28, width: '100%', maxWidth: 480
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 20, color: 'var(--primary)' }}>📞 Create Call Order</div>
            
            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>Customer Phone *</label>
                <input className="input" placeholder="e.g. 9876543210" value={callOrderForm.customerPhone} onChange={e => setCallOrderForm({...callOrderForm, customerPhone: e.target.value})} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>Assign to Shop *</label>
                <select className="input" value={callOrderForm.shopId} onChange={e => setCallOrderForm({...callOrderForm, shopId: e.target.value})} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}>
                  <option value="">दुकानाची निवड करा...</option>
                  {shops.map(s => <option key={s.id} value={s.id}>{s.shopName || s.name} ({s.village || 'Sangola'})</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>Items Description *</label>
                <textarea className="input" placeholder="e.g. 2 kg Sugar, 1 Parle-G" value={callOrderForm.itemsDesc} onChange={e => setCallOrderForm({...callOrderForm, itemsDesc: e.target.value})} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', minHeight: 60 }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>Estimated Amount (₹)</label>
                  <input type="number" className="input" placeholder="e.g. 150" value={callOrderForm.amount} onChange={e => setCallOrderForm({...callOrderForm, amount: e.target.value})} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>Village</label>
                  <input className="input" placeholder="e.g. Sangola" value={callOrderForm.village} onChange={e => setCallOrderForm({...callOrderForm, village: e.target.value})} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>Landmark / Address</label>
                <input className="input" placeholder="e.g. Near Temple" value={callOrderForm.landmark} onChange={e => setCallOrderForm({...callOrderForm, landmark: e.target.value})} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowCallOrderModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleCreateCallOrder}>Create & Assign Order</button>
            </div>
          </div>
        </div>
      )}

      {/* AI Dispatch Status & Decision Modal */}
      {showAIDispatchModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1050, padding: 20
        }}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 20, padding: 30, width: '100%', maxWidth: 640,
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', maxHeight: '90vh'
          }} onClick={e => e.stopPropagation()}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 15 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24, animation: 'pulse 2s infinite' }}>🤖</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--primary)' }}>Smart AI Dispatch Engine</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Bipartite Matching & Route optimization matching</div>
                </div>
              </div>
              <span className={`badge ${aiStatus === 'scanning' ? 'warning' : aiStatus === 'calculated' ? 'info' : aiStatus === 'saving' ? 'primary' : 'success'}`}>
                {aiStatus.toUpperCase()}
              </span>
            </div>

            {/* AI Calculation Process Logs */}
            <div style={{
              background: '#0d1117', border: '1px solid var(--border)',
              borderRadius: 10, padding: 15, fontFamily: 'monospace',
              fontSize: 12, color: '#39ff14', overflowY: 'auto',
              flex: 1, minHeight: 180, maxHeight: 220, marginBottom: 20,
              display: 'flex', flexDirection: 'column', gap: 6,
              boxShadow: 'inset 0 0 10px rgba(0,0,0,0.8)'
            }}>
              {aiLogs.map((log, i) => (
                <div key={i} style={{ lineBreak: 'anywhere' }}>
                  {log.startsWith('❌') ? <span style={{ color: '#ff3b30' }}>{log}</span> :
                   log.startsWith('✅') || log.startsWith('🎉') ? <span style={{ color: '#34c759' }}>{log}</span> :
                   log.startsWith('  →') ? <span style={{ color: '#00d2ff' }}>{log}</span> :
                   <span>{log}</span>}
                </div>
              ))}
              {aiStatus === 'scanning' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#F59E0B', marginTop: 4 }}>
                  <span className="spinner" style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid #F59E0B', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  Evaluating candidate weights...
                </div>
              )}
            </div>

            {/* AI Proposed Matching Results */}
            {aiProposedMatches.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 2, overflowY: 'auto', marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: 'var(--text-primary)' }}>🤖 AI Recommended Rider Assignments:</div>
                <div className="table-wrapper" style={{ margin: 0, border: '1px solid var(--border)', borderRadius: 10 }}>
                  <table style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Zone</th>
                        <th>Recommended Rider</th>
                        <th>Dist</th>
                        <th>Tasks</th>
                        <th>Match Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiProposedMatches.map((m, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 600 }}>{m.orderId}</td>
                          <td><span className="badge muted" style={{ fontSize: 10 }}>{m.village}</span></td>
                          <td style={{ fontWeight: 600 }}>🛵 {m.riderName}</td>
                          <td>{m.dist} km</td>
                          <td>{m.load} active</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 45, background: 'var(--bg)', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${m.score}%`, background: m.score > 80 ? 'var(--success)' : m.score > 50 ? 'var(--info)' : 'var(--warning)', height: '100%' }} />
                              </div>
                              <span style={{ fontWeight: 700, color: m.score > 80 ? 'var(--success)' : 'var(--info)' }}>{m.score}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Modal Controls */}
            <div style={{ display: 'flex', gap: 12, marginTop: 'auto', borderTop: '1px solid var(--border)', paddingTop: 15 }}>
              <button 
                className="btn btn-secondary" 
                style={{ flex: 1, justifyContent: 'center' }} 
                onClick={() => setShowAIDispatchModal(false)}
                disabled={aiStatus === 'saving'}
              >
                Close
              </button>
              {aiStatus === 'calculated' && (
                <button 
                  className="btn btn-primary" 
                  style={{ flex: 2, justifyContent: 'center', background: 'linear-gradient(135deg, var(--success), #15803d)', border: 'none' }} 
                  onClick={confirmAIDispatch}
                >
                  Approve & Dispatch {aiProposedMatches.length} Order(s)
                </button>
              )}
              {aiStatus === 'saving' && (
                <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={true}>
                  Saving to Cloud Database...
                </button>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
